import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Listbox } from '@/components/ui/listbox';
import { Textarea } from '@/components/ui/textarea';

export const ASSET_KINDS = ['building', 'furniture', 'equipment', 'vehicle', 'other'] as const;
export const ASSET_CONDITIONS = [
  'excellent',
  'good',
  'fair',
  'poor',
  'unserviceable',
] as const;

export type AssetKind = (typeof ASSET_KINDS)[number];
export type AssetCondition = (typeof ASSET_CONDITIONS)[number];

export interface AssetFormValues {
  name: string;
  kind: AssetKind;
  condition: AssetCondition;
  acquiredOn: string | null;
  notes: string;
  registrationNumber: string;
}

export interface AssetRow {
  id: string;
  name: string;
  kind: AssetKind;
  condition: AssetCondition;
  status: 'active' | 'on_loan' | 'under_repair' | 'disposed';
  acquiredOn: string | null;
  notes: string | null;
  registrationNumber: string | null;
}

interface AssetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset?: AssetRow | null;
  submitting?: boolean;
  onSubmit: (values: AssetFormValues) => void | Promise<void>;
}

const emptyValues = (): AssetFormValues => ({
  name: '',
  kind: 'equipment',
  condition: 'good',
  acquiredOn: null,
  notes: '',
  registrationNumber: '',
});

export const AssetDialog = ({
  open,
  onOpenChange,
  asset,
  submitting = false,
  onSubmit,
}: AssetDialogProps) => {
  const { t } = useTranslation('assets');
  const { t: tCommon } = useTranslation();
  const [values, setValues] = useState<AssetFormValues>(emptyValues);

  useEffect(() => {
    if (!open) return;
    if (asset) {
      setValues({
        name: asset.name,
        kind: asset.kind,
        condition: asset.condition,
        acquiredOn: asset.acquiredOn,
        notes: asset.notes ?? '',
        registrationNumber: asset.registrationNumber ?? '',
      });
    } else {
      setValues(emptyValues());
    }
  }, [asset, open]);

  const canSubmit = values.name.trim().length > 0;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={asset ? t('edit') : t('create')}
      submitLabel={t('save')}
      cancelLabel={tCommon('action.cancel')}
      submitting={submitting}
      submitDisabled={!canSubmit}
      size="lg"
      onSubmit={() =>
        onSubmit({
          ...values,
          name: values.name.trim(),
          notes: values.notes.trim(),
          registrationNumber: values.registrationNumber.trim(),
        })
      }
    >
      <FormField label={t('fields.name')} required>
        {(field) => (
          <Input
            id={field.id}
            value={values.name}
            onChange={(event) => setValues((current) => ({ ...current, name: event.target.value }))}
            aria-describedby={field['aria-describedby']}
            aria-invalid={field.invalid}
          />
        )}
      </FormField>

      <FormField label={t('fields.kind')} required>
        {(field) => (
          <Listbox
            id={field.id}
            value={values.kind}
            onChange={(kind) =>
              setValues((current) => ({ ...current, kind: kind as AssetKind }))
            }
            options={ASSET_KINDS.map((kind) => ({
              value: kind,
              label: t(`kind.${kind}`),
            }))}
            popoverPortal="dialog"
            aria-describedby={field['aria-describedby']}
          />
        )}
      </FormField>

      <FormField label={t('fields.condition')} required>
        {(field) => (
          <Listbox
            id={field.id}
            value={values.condition}
            onChange={(condition) =>
              setValues((current) => ({
                ...current,
                condition: condition as AssetCondition,
              }))
            }
            options={ASSET_CONDITIONS.map((condition) => ({
              value: condition,
              label: t(`condition.${condition}`),
            }))}
            popoverPortal="dialog"
            aria-describedby={field['aria-describedby']}
          />
        )}
      </FormField>

      <FormField label={t('fields.acquiredOn')}>
        {(field) => (
          <DatePicker
            id={field.id}
            value={values.acquiredOn}
            onChange={(acquiredOn) => setValues((current) => ({ ...current, acquiredOn }))}
            popoverPortal="dialog"
            aria-describedby={field['aria-describedby']}
          />
        )}
      </FormField>

      {values.kind === 'vehicle' ? (
        <FormField label={t('fields.registrationNumber')}>
          {(field) => (
            <Input
              id={field.id}
              value={values.registrationNumber}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  registrationNumber: event.target.value,
                }))
              }
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>
      ) : null}

      <FormField label={t('fields.notes')}>
        {(field) => (
          <Textarea
            id={field.id}
            value={values.notes}
            onChange={(event) =>
              setValues((current) => ({ ...current, notes: event.target.value }))
            }
            rows={3}
            aria-describedby={field['aria-describedby']}
          />
        )}
      </FormField>
    </FormDialog>
  );
};
